"use client"

import { useEffect, useRef } from "react"
import L from "leaflet"

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
})

const MapView = ({ devices, alerts }) => {
  const mapRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const markersRef = useRef([])

  useEffect(() => {
    if (!mapInstanceRef.current) {
      // Initialize map
      mapInstanceRef.current = L.map(mapRef.current).setView([28.6139, 77.209], 10)

      // Add tile layer
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors",
      }).addTo(mapInstanceRef.current)
    }

    // Clear existing markers
    markersRef.current.forEach((marker) => {
      mapInstanceRef.current.removeLayer(marker)
    })
    markersRef.current = []

    // Add device markers
    devices.forEach((device) => {
      const criticalAlerts = alerts.filter(
        (alert) => alert.deviceId === device.id && alert.severity === "critical" && !alert.acknowledged,
      )

      const getMarkerColor = () => {
        if (criticalAlerts.length > 0) return "#ef4444" // red
        switch (device.status) {
          case "active":
            return "#10b981" // green
          case "warning":
            return "#f59e0b" // yellow
          default:
            return "#6b7280" // gray
        }
      }

      // Create custom icon
      const customIcon = L.divIcon({
        className: "custom-marker",
        html: `
          <div style="
            background-color: ${getMarkerColor()};
            width: 16px;
            height: 16px;
            border-radius: 50%;
            border: 2px solid white;
            box-shadow: 0 1px 3px rgba(0,0,0,0.3);
          "></div>
        `,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      })

      const marker = L.marker([device.location.lat, device.location.lng], {
        icon: customIcon,
      }).addTo(mapInstanceRef.current)

      // Add popup
      const popupContent = `
        <div class="p-2">
          <h3 class="font-bold text-sm">${device.name}</h3>
          <p class="text-xs text-gray-600">${device.id}</p>
          <p class="text-xs">${device.location.address}</p>
          <div class="mt-2">
            <span class="inline-block px-2 py-1 text-xs rounded ${
              device.status === "active"
                ? "bg-green-100 text-green-800"
                : device.status === "warning"
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-red-100 text-red-800"
            }">
              ${device.status.toUpperCase()}
            </span>
          </div>
          <div class="mt-1 text-xs">
            <div>Battery: ${device.battery}%</div>
            <div>Health: ${device.health}</div>
          </div>
          ${
            criticalAlerts.length > 0
              ? `
            <div class="mt-2 p-2 bg-red-50 rounded">
              <p class="text-xs text-red-800 font-medium">
                ${criticalAlerts.length} Critical Alert${criticalAlerts.length > 1 ? "s" : ""}
              </p>
            </div>
          `
              : ""
          }
        </div>
      `

      marker.bindPopup(popupContent)
      markersRef.current.push(marker)
    })

    return () => {
      // Cleanup on unmount
      if (mapInstanceRef.current) {
        markersRef.current.forEach((marker) => {
          mapInstanceRef.current.removeLayer(marker)
        })
      }
    }
  }, [devices, alerts])

  return <div ref={mapRef} style={{ height: "100%", width: "100%" }} />
}

export default MapView

